import { AfterViewInit, ChangeDetectorRef, Component, effect, Input, OnDestroy, OnInit } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import { PatientTargetDataModel, PatientTargetModel, PatientTargetStatus, AlarmOption } from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
@Component({
  selector: 'app-box-duration',
  templateUrl: './box-duration.component.html',
  standalone: false,
})
export class BoxDurationComponent extends BaseBoxComponent implements OnDestroy {
  text: string | undefined = '-';
  amountOfTimes: number = 0;
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() target: PatientTargetModel | undefined = undefined;
  @Input() isPreview?: boolean;
  targetID: number = 0;
  isStarted: boolean = false;
  timerValue: number = 0;
  private startTime: number = 0;
  times: { ID?: number, Text: string, Duration: number, RecordedDate: string, FormattedDate: string }[] = [
  ];
  targetData: PatientTargetDataModel[] = [];

  // Bulk add fields
  bulkStart: string = '';
  bulkEnd: string = '';
  bulkOccurrences: number | null = null;

  timeOut: any;
  remainingTrials: number = 0;
  // Add a flag to track when we're intentionally stopping the timer
  private manuallyStoppedTimer: boolean = false;
  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    effect(() => {
      this.patientSessionService.dataChanged();
      const targetData = this.patientSessionService.getTargetData(this.targetID);
      if (targetData) {
        this.targetData = targetData.filter(t => !t.DeletedDate);
        this.remainingTrials = (this.target?.DesiredTrials ?? 0) - this.targetData.length;
        if (this.remainingTrials < 0) {
          this.remainingTrials = 0;
        }

        if (this.target?.TargetStatus === PatientTargetStatus.Mastered || this.target?.TargetStatus === PatientTargetStatus.Closed || this.target?.TargetStatus === PatientTargetStatus.Fluent) {
          this.remainingTrials = 0;
        }   

        this.times = this.targetData.map(t => ({ ID: t.ID, Text: t.TextValue ?? '', Duration: t.IntValue ?? 0, RecordedDate: t.RecordedDate ?? '', FormattedDate: this.formatDate(new Date(t.RecordedDate ?? '')) }));
        this.amountOfTimes = this.times.length;
      }
      else {
        this.targetData = [];
        this.times = [];
        this.amountOfTimes = 0;
      }
    });

    effect(() => {
      this.patientSessionService.dataChanged();
      const targetsRunningData = this.patientSessionService.getTargetsRunningData(this.patientSessionService.getPatientSession()?.ID ?? -1,this.targetID);
      if (targetsRunningData) {
        // Only update if we're not manually stopping the timer
        if (!this.manuallyStoppedTimer) {
          const wasStarted = this.isStarted;
          this.isStarted = targetsRunningData.Data.isStarted;
          this.startTime = targetsRunningData.Data.startTime;
          this.timerValue = this.isStarted
            ? this.getCurrentTimerValue()
            : targetsRunningData.Data.timerValue;
          
          // Only setup the timer if it was already running or is being started for the first time
          if (this.isStarted && !wasStarted) {           
              this.updateTimer();
          }
        } 
        // If we manually stopped, make sure the persisted state reflects that
        else if (this.manuallyStoppedTimer && targetsRunningData.Data.isStarted) {
          // Override with our stopped state
          this.saveTargetRunningData();
        }
      } else {
        this.isStarted = false;
        this.startTime = 0;
        this.timerValue = 0;
      }
    });
  }

  ngOnInit(): void {
    this.text = this.target?.TargetName ?? '-';
    this.targetID = this.target?.ID ?? 0;
    
  }

  override doLongPress(): void {
    this.showExtra();
  }

  startTimer() {
    this.isStarted = true;
    this.startTime = Date.now();
    this.timerValue = 0;
    this.updateTimer();
    this.saveTargetRunningData();
  }

  private updateTimer(): void {
    if (this.isStarted) {
      this.timerValue = this.getCurrentTimerValue();

      this.ringAlarmIfNeeded(
        this.target?.AlarmOption ?? AlarmOption.NoAlarm, 
        this.timerValue, 
        this.target?.Duration ?? 0, 
        this.target?.AlarmSoundOption ?? 1,
        true
      );
      
      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }
      this.timeOut = setTimeout(() => {
        this.updateTimer();
      }, 1000);
    }else {
      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }
    }
  }

  private getCurrentTimerValue(): number {
    return Math.max(0, Math.floor((Date.now() - this.startTime) / 1000));
  }

  // returns the duration of the target in mm:ss format
  getDurationFormatted() {
    const durationInSeconds = this.target?.Duration ?? 0;
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = durationInSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  getDurationForDisplay() {
    const durationInSeconds = this.target?.Duration ?? 0;
    if (durationInSeconds < 60) {
      return durationInSeconds + " seconds";
    }
    return durationInSeconds / 60 + " minutes";
  }

  override ngOnDestroy() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    // Call the parent class's ngOnDestroy to ensure proper cleanup
    super.ngOnDestroy();
  }


  saveTargetRunningData() {
    this.patientSessionService.setTargetsRunningData(this.patientSessionService.getPatientSession()?.ID ?? -1, this.targetID, { isDurationTarget:true, isStarted: this.isStarted, startTime: this.startTime, timerValue: this.timerValue });
  }

  stopTimer() {
    // Set the flag to indicate we're manually stopping the timer
    this.manuallyStoppedTimer = true;
    if (this.timeOut) {
      clearTimeout(this.timeOut);
      this.timeOut = null; // Clear the timeOut reference
    }
    if (this.timerValue > 0) {
      this.saveTime();
      this.amountOfTimes = this.times.length;
    }
    this.timerValue = 0;
    this.isStarted = false;
    this.startTime = 0;
    
    // Make sure the persisted state is updated immediately
    this.saveTargetRunningData();
    
    // Reset the flag after a short delay to prevent immediate restarting
    setTimeout(() => { 
      this.manuallyStoppedTimer = false;
    }, 1500);
  }

  saveTime() {
    const currentDate = new Date();
    const formattedDate = this.formatDate(currentDate);
    const formattedTime = this.timerString();
    this.patientSessionService.addTargetData({
      PatientTargetID: this.targetID,
      TextValue: formattedTime,
      IntValue: this.timerValue,
      RecordedDate: currentDate.toServerString()
    });
    this.times.unshift({ Text: formattedTime, Duration: this.timerValue, RecordedDate: currentDate.toServerString(), FormattedDate: formattedDate });
  }

  timerString() {
    return Utils.formatTime(this.timerValue);
  }

  deleteData(data?: any) {
    if (data) {
      //confirm the deletion
      const confirmed = this.alertService.confirm('Are you sure you want to delete this data?', 'Delete Data');
      confirmed.subscribe(result => {
        if (result) {
          let targetDataToSave: PatientTargetDataModel | undefined = this.targetData.find(t =>
            t.RecordedDate === data.RecordedDate
          );
  
          if (targetDataToSave) {
            console.log('Updating existing data:', targetDataToSave);
            targetDataToSave.DeletedDate = new Date().toServerString();
  
            this.patientSessionService.saveTargetData(targetDataToSave);
          }
        }
      });
    }
  }

}
